import React, { useCallback } from "react";
import { FileDown, FileSpreadsheet, Pizza } from "lucide-react";
import { toast } from "react-toastify";

import { BrazilDate } from "../../../utils/BrazilDate";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import {
  buildFiltrosResumo,
  exportRelatorioSaboresPdf,
  exportRelatorioSaboresXlsx,
} from "../utils/relatorioSaboresExport";

interface RelatorioSaboresToolbarProps {
  variant?: "full" | "exportsOnly";
}

const RelatorioSaboresToolbar: React.FC<RelatorioSaboresToolbarProps> = ({
  variant = "full",
}) => {
  const {
    lojaOptions,
    allTamanhoOptions,
    saborOptions,
    allUsuarioOptions,
    linhas,
    isLoading,
    totalQuantidade,
    appliedFilters,
  } = useRelatorioSabores();

  const buildExportPayload = useCallback(() => {
    if (!appliedFilters) return null;
    const linhasLojaTamanhoSaborMap = new Map<
      string,
      {
        lojaNome: string;
        tamanhoNome: string;
        saborNome: string;
        quantidade: number;
      }
    >();

    for (const linha of linhas) {
      const partes = linha.partes > 0 ? linha.partes : 1;
      const quantidadeFracionada = linha.quantidade / partes;
      const key = `${linha.lojaId}-${linha.tamanhoId}-${linha.saborId}`;
      const current = linhasLojaTamanhoSaborMap.get(key);

      if (current) {
        current.quantidade += quantidadeFracionada;
        continue;
      }

      linhasLojaTamanhoSaborMap.set(key, {
        lojaNome: linha.lojaNome,
        tamanhoNome: linha.tamanhoNome,
        saborNome: linha.saborNome,
        quantidade: quantidadeFracionada,
      });
    }

    const linhasLojaTamanhoSabor = Array.from(
      linhasLojaTamanhoSaborMap.values()
    ).sort((a, b) => {
      if (b.quantidade !== a.quantidade) return b.quantidade - a.quantidade;
      const byLoja = a.lojaNome.localeCompare(b.lojaNome, "pt-BR");
      if (byLoja !== 0) return byLoja;
      const byTamanho = a.tamanhoNome.localeCompare(b.tamanhoNome, "pt-BR");
      if (byTamanho !== 0) return byTamanho;
      return a.saborNome.localeCompare(b.saborNome, "pt-BR");
    });

    const filtros = buildFiltrosResumo(
      appliedFilters,
      lojaOptions,
      allTamanhoOptions,
      saborOptions,
      allUsuarioOptions
    );
    return {
      periodoInicioISO: appliedFilters.dataInicio,
      periodoFimISO: appliedFilters.dataFim,
      filtros,
      linhas: linhas.map((r) => ({
        lojaNome: r.lojaNome,
        rotulo: r.rotulo,
        quantidade: r.quantidade,
      })),
      linhasLojaTamanhoSabor,
      totalQuantidade,
      geradoEmLabel: BrazilDate.formatBR(new Date(), true),
    };
  }, [
    appliedFilters,
    lojaOptions,
    allTamanhoOptions,
    saborOptions,
    allUsuarioOptions,
    linhas,
    totalQuantidade,
  ]);

  const handleExportXlsx = useCallback(() => {
    if (linhas.length === 0) {
      toast.warning("Não há dados para exportar. Aplique os filtros primeiro.");
      return;
    }
    try {
      const payload = buildExportPayload();
      if (!payload) {
        toast.error("Filtros não aplicados.");
        return;
      }
      exportRelatorioSaboresXlsx(payload);
      toast.success("Planilha baixada.");
    } catch {
      toast.error("Não foi possível gerar a planilha.");
    }
  }, [linhas.length, buildExportPayload]);

  const handleExportPdf = useCallback(() => {
    if (linhas.length === 0) {
      toast.warning("Não há dados para exportar. Aplique os filtros primeiro.");
      return;
    }
    try {
      const payload = buildExportPayload();
      if (!payload) {
        toast.error("Filtros não aplicados.");
        return;
      }
      exportRelatorioSaboresPdf(payload);
      toast.success("PDF gerado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [linhas.length, buildExportPayload]);

  const exportButtons = (
    <div className="flex flex-wrap gap-2 sm:justify-end">
      <button
        type="button"
        onClick={handleExportXlsx}
        disabled={linhas.length === 0 || isLoading}
        className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-800 shadow-sm transition-colors hover:border-primary/30 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FileSpreadsheet size={18} className="text-emerald-700" />
        Excel
      </button>
      <button
        type="button"
        onClick={handleExportPdf}
        disabled={linhas.length === 0 || isLoading}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FileDown size={18} />
        PDF (A4)
      </button>
    </div>
  );

  if (variant === "exportsOnly") {
    return exportButtons;
  }

  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Pizza size={22} strokeWidth={2} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            Pizzas por sabor
          </h2>
          <p className="text-sm text-neutral-600">
            Meio a meio aparece como 1/2, 1/3, etc., conforme quantos sabores na
            produção.
          </p>
        </div>
      </div>
      {exportButtons}
    </div>
  );
};

export default RelatorioSaboresToolbar;
